import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavigationButton from "../../components/ui/NavigationButton";
import { Card, CardContent } from "../../components/ui/card";
import StaffRequirementsTable from "../../components/forms/staffRequirementsForm/StaffRequirementsTable";
import BackButton from "../../components/ui/BackNavigationButton";
import Tooltip from "../../components/ui/ToolTip";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { pages } from "../../components/utils/pages-links";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import { useAlert } from "../../context/alert/AlertProvider";
import { useTranslation } from 'react-i18next';
import { usePageTooltips } from "../../hooks/usePageTooltips";
import useAutoSave from "../../hooks/useAutoSave";

const StaffRequirementPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(0);
    const {showAlert} = useAlert();
    const isExampleMode = location.state?.isExample;
    const staffRequirementTableRef = useRef(null);
    const nextButtonRef = useRef(null);
    const { t } = useTranslation();
    const tooltips = usePageTooltips();

    const tutorialSteps = [
        {
            title: t("tutorial.staffRequirement.title"),
            description: t("tutorial.staffRequirement.description"),
            position:'center'
        },
        {
            title: t("tutorial.staffRequirement.daily.title"),
            description: t("tutorial.staffRequirement.daily.description"),
            position:"top-right",
            elementRef: staffRequirementTableRef,
        },
        {
            title: t("tutorial.staffRequirement.next.title"),
            description: t("tutorial.staffRequirement.next.description"),
            position: "next-button",
            elementRef: nextButtonRef,
        },
    ];

    const [requirements, setRequirements] = useState({
        max: Array(7).fill(''),
        min: Array(7).fill('')
    });

    useAutoSave('staffRequirements', requirements);

    useEffect(() => {
        if(isExampleMode) {
            setShowTutorial(true);
        }

        const savedEmployees = JSON.parse(sessionStorage.getItem('employees') || '[]');
        const employeeCount = savedEmployees.length;
        
        const defaultMax = Array(7).fill(employeeCount.toString());
        const defaultMin = Array(7).fill('1');

        const savedRequirements = JSON.parse(sessionStorage.getItem('staffRequirements'));
        if (savedRequirements) {
            setRequirements({
                max: savedRequirements.max.slice(0, 7),
                min: savedRequirements.min.slice(0, 7)
            });
        } else {
            setRequirements({
                max: defaultMax,
                min: defaultMin
            });
        }
    }, [isExampleMode]);

    const handleInputChange = (type, index, value) => {
        if (index >= 7) return; 
        setRequirements(prev => ({
            ...prev,
            [type]: prev[type].map((v, i) => i === index ? value : v)
        }));
    }

    const validateRequirements = () => {
        return requirements.min.slice(0, 7).every(v => v !== '');
    }

    const handleNext = () => {
        if (!validateRequirements()) {
            showAlert({
                title: t("alert.error"),
                description: t("alert.error.minValues"),
                variant:'destructive'
            })
            return;
        }
        
        navigate('/form/distribution', {
            state: { isExample: isExampleMode}
        });
    };

    const handleNextTutorialStep = () => {
        if(tutorialStep < tutorialSteps.length - 1){
            setTutorialStep(prev => prev + 1);
        } else {
            setShowTutorial(false);
        }
    };

    const handleBack = () => {
        navigate('/form/transitions' , {
            state: { isExample: isExampleMode }
        });
    }

    return (
        <div className="-mt-16">
      <Breadcrumbs pages={pages}/>
      <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4">
            <BackButton onClick={handleBack} className="order-2 md:order-1"/>
            <NavigationButton 
              ref={nextButtonRef} 
              onClick={handleNext}
              className="order-1 md:order-2 w-full md:w-auto"
            />
          </div>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
                <h1 className="text-xl md:text-2xl font-bold">
                  {t("form.staffRequirement.title")}
                </h1>
                <div className="flex items-center">
                  <Tooltip content={tooltips.staffRequirement}/>
                </div>
              </div>

              <div className="overflow-x-auto">
                <StaffRequirementsTable 
                  ref={staffRequirementTableRef}
                  requirements={requirements}
                  onInputChange={handleInputChange}
                  isDisabled={isExampleMode}
                  translations={t}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
            <TutorialOverlay
                isVisible={showTutorial}
                onClose={() => setShowTutorial(false)}
                title={tutorialSteps[tutorialStep]?.title}
                description={tutorialSteps[tutorialStep]?.description}
                position={tutorialSteps[tutorialStep]?.position}
                targetRef={tutorialSteps[tutorialStep]?.elementRef}
                onNext={handleNextTutorialStep}
                isLastStep={tutorialStep === tutorialSteps.length -1 }
            />
        </div>
    );
}

export default StaffRequirementPage;